import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { filter, finalize, map, take, tap } from 'rxjs/operators';
import { CONTENT_TYPES } from 'src/app/shared/constants';
import {
  AlertService,
  ToastTypes,
} from 'src/app/shared/services/alert.service';
import * as moment from 'moment';

import { Reset as ResetApplicationUsers } from '../state/_actions/settings/application-users.actions';
import { Reset as ResetCopyApps } from '../state/_actions/settings/copy-app.actions';
import { Reset as ResetDashboards } from '../state/_actions/settings/dashboards.action';
import { Reset as ResetPages } from '../state/_actions/settings/pages.action';
import { Reset as ResetRoles } from '../state/_actions/settings/roles.actions';
import { Reset as ResetApplication } from '../state/_actions/application.actions';
import { Reset as ResetAuth, Token, LoginUserFailed } from '../state/_actions/auth.actions';
import { Reset as ResetClient } from '../state/_actions/clients.actions';
import { Reset as ResetCompany } from '../state/_actions/company.actions';
import { Reset as ResetField } from '../state/_actions/field.actions';
import { Reset as ResetForms } from '../state/_actions/form.actions';
import { Reset as ResetIgnatius } from '../state/_actions/ignatius.actions';
import { Reset as ResetImports } from '../state/_actions/import.actions';
import { Reset as ResetNotification } from '../state/_actions/notification.actions';
import { Reset as ResetPurges } from '../state/_actions/purge.actions';
import { Reset as ResetRelations } from '../state/_actions/relation.actions';
import { Reset as ResetReports } from '../state/_actions/report.actions';
import { Reset as ResetNavigation } from '../state/_actions/table.actions';
import { Profile, Reset as ResetUser } from '../state/_actions/user.actions';
import { Credentials, TokenAPI } from '../state/_models/auth.models';
import {
  getAuth,
  getAuthError,
  getLoginResponse,
} from '../state/_selectors/auth.selector';
import { getUserProfile } from '../state/_selectors/user.selector';
import { StorageService } from './storage.service';
import { RuntimeConfigService } from './runtime-config.service';
import { RouteService } from './route.service';
import { CookieService } from 'ngx-cookie';
import { environment } from 'src/environments/environment';
import { AuthService as loginService } from '../state/_services/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Title } from '@angular/platform-browser';
import { IdleService } from './idle.service';
import { RefreshTokenModalComponent } from 'src/app/modules/auth/refresh-token-modal/refresh-token-modal.component';
import { AuthService as AuthAPIService } from '../state/_services/auth.service';
import { AuthService as Auth0} from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { WindowRefService } from './window-ref.service';
import { SubSink } from 'subsink';
import { Roles } from 'src/app/shared/enums/role.enum';
import { NavigationService } from './navigation.service';
import { LoadingService } from './loading.service';
import { ApplicationService } from '../state/_services/application.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { LaunchDarklyService } from './launchdarkly.service';

/**
 * Validates a single path segment for in-app redirects to `/apps/:id`.
 * Rejects values that could enable open redirects (slashes, "..", "@", ":", encoded URLs, etc.).
 */
export function sanitizeAppPathSegmentForRedirect(value: unknown): string | null {
  let s: string;
  if (typeof value === 'string') {
    s = value.trim();
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    s = String(value).trim();
  } else {
    return null;
  }
  if (!s || s.length > 128) {
    return null;
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(s)) {
    return null;
  }
  return s;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  isLoggedIn: any = false;
  private tokenApiData: TokenAPI | null = null;
  private mlTokenApiData: TokenAPI | null = null;
  private ml_access_token: string | null = null;
  private ml_refresh_token: string | null = null;
  private access_token: string | null = null;
  private refresh_token: string | null = null;
  public username: string | null = null;
  public userProfile: any = null;
  public rememberMe: boolean = true;
  public isRedirectPerformed: boolean = true;
  private titleInterval: any = null;
  private logoutTimeout: any = null;
  private TOKEN_URL = '/token';
  public refreshTokenPromise: any = null;
  readonly IDLE_TIME = 3480; // in sec
  readonly IDLE_TIMEOUT = 120; // in sec
  subsink: SubSink = new SubSink();
  tokenSubscription: Array<Subscription> = [];
  loginLogoutSubject = new Subject();
  userDataSubject = new BehaviorSubject(0);
  private tokenData: any;
  private mlTokenData: any = {};
  private pendingLoginResponse: { tokenData: any; response: any } | null = null;
  isProfileDataFetched:boolean = false;
  isImpersonated:boolean = false;

  constructor(
    private readonly storage: StorageService,
    private readonly runtimeConfig: RuntimeConfigService,
    private readonly store: Store,
    private readonly alert: AlertService,
    private readonly route: RouteService,
    private readonly loginService: loginService,
    private readonly cookie: CookieService,
    private readonly http: HttpClient,
    private readonly modal: NgbModal,
    private readonly idle: IdleService,
    private readonly title: Title,
    private readonly authAPISerive: AuthAPIService,
    private readonly auth0Service: Auth0,
    private readonly router: Router,
    private readonly windowRef: WindowRefService,
    private readonly navigationService: NavigationService,
    private readonly loadingService: LoadingService,
    private readonly applicationService: ApplicationService,
    private readonly ldService: LaunchDarklyService,
  ) {
    this.idleSubscribe();
    this.cacheAuthData();
    this.subscriptions();
    // this.getExternalLoginState();
  }

  /**
   * Clear Auth0 SPA SDK artifacts in storage to avoid cross-IdP leaks and invalid state
   */
  private clearAuth0Artifacts = () => {
    try {
      const removeMatchingKeys = (storage: Storage) => {
        const keysToRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i) as string;
          if (!key) continue;
          // Auth0 SPA SDK keys
          if (
            key.startsWith('a0.spajs.') ||
            key.includes('@@auth0spajs@@') ||
            key.includes('auth0.is.authenticated')
          ) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => storage.removeItem(k));
      };

      removeMatchingKeys(localStorage);
      removeMatchingKeys(sessionStorage);
      // Our callback one-shot marker
      sessionStorage.removeItem('otherAuth0Handled');
    } catch (e) {
      // no-op
    }
  };

  get isSuperAdmin() {
    return this.userProfile?.Role === Roles.superAdmin;
  }

  /**
   * Cache all localstorage data
   */
  private cacheAuthData = () => {
    // Bypass session check for callback routes so the callback handler can run
    if (window.location.pathname === '/auth/callback' || window.location.pathname === '/callback-handler') {
      return;
    }

    // Check if the URL already has an appId parameter - if so, prioritize it
    const urlAppIdMatch = /\/auth\/login\/([^/?#]+)/.exec(window.location.pathname);
    const urlAppId = urlAppIdMatch?.[1];

    // If URL has appId, update storage and skip redirect logic
    if (urlAppId) {
      this.storage.savePortalId(urlAppId);
      // Continue to cache tokens below, but skip the redirect logic
    } else {
      // Only honor stored redirect/appId when not authenticated yet, or when already on an auth route
      const hasToken = !!this.storage.getToken();
      const onAuthRoute = window.location.pathname.startsWith('/auth/');
      const onRegisterRoute = window.location.pathname.startsWith('/auth/register');
      const onPasswordRecoveryRoute = window.location.pathname.startsWith('/auth/passwordrecovery');
      const storedRedirect = this.storage.getRedirectURL();
      const storedPortal = this.storage.getPortalId();
      const isCallback = window.location.href.includes('/callback-handler');
      if ((!!storedRedirect || !!storedPortal) && !isCallback && (!hasToken || onAuthRoute) && !onPasswordRecoveryRoute && !onRegisterRoute) {
        const redirectUrl = storedRedirect ?? '';
        let targetPortal = storedPortal ?? '';
        if (!targetPortal && redirectUrl) {
          const candidate = /\/apps\/([^\/?#]+)/.exec(redirectUrl)?.[1];
          if (candidate) targetPortal = candidate;
        }
        const commands = targetPortal ? ['auth', 'login', targetPortal] : ['auth', 'login'];
        this.route.navigate(commands as any, {
          queryParams: {
            ...(redirectUrl ? { redirectTo: redirectUrl } : {}),
          },
          replaceUrl: true,
        });
      }
    }
    const token = this.storage.getToken();
    const refresh = this.storage.getRefresh();
    if (token) {
      this.access_token = token;
      this.refresh_token = refresh;
      this.isLoggedIn = true;
      this.idle.watch();
    } else {
      // keep logged-out state without forcing navigation
      this.access_token = null;
      this.refresh_token = null;
      this.isLoggedIn = false;
    }
  };

  /**
   * Get access token
   */
  getAccessToken = (): string | null => this.access_token;

  /**
   * get refesh token
   */
  getRefreshToken = (): string | null => this.refresh_token;

  /**
   * Get access token
   */
  getMLAccessToken = (): string | null => this.ml_access_token;

  /**
   * get refesh token
   */
  getMLRefreshToken = (): string | null => this.ml_refresh_token;

  /**
   * revoke token
   */
  revokeToken = () => {
    return this.loginService.revokeToken(
      this.getRefreshToken()!,
      this.getHeaders()
    );
  };
  /**
   * Resolves whether runtime secrets feature flag is enabled.
   * Caches the result so subsequent calls are synchronous.
   */
  private get isImpersonateUserIssueFixEnabled(): boolean {
    return !!this.ldService
      .getFlagValueSync(FEATURE_FLAGS.ENABLE_IMPERSONATE_USER_ISSUE_FIX, false);
  }

  /**
   * Perform logout operation by clearing localstorage
   * All states need to be reset here
   */
  logout = (
    setRedirectTo: boolean = true,
    isClearStorage: boolean = false,
    isPortal: string | null = null,
    forceDuringCallback: boolean = false
  ) => {
    // Prevent logout redirects during Auth0 callback processing unless forced
    this.navigationService.showGlobalNav.emit('hide');
    if (window.location.pathname === '/callback-handler' && !forceDuringCallback) {

      return;
    }
    // Ensure any previous Auth0 SPA artifacts are cleared so next login starts clean
    this.clearAuth0Artifacts();
    // Clear bootstrap token and runtime config so next login fetches fresh values
    if (this.runtimeConfig.isFeatureEnabled()) {
      this.storage.clearBootstrapToken();
      this.runtimeConfig.clearConfig();
    }
    // Aggressively clear local auth state immediately to avoid cross-IdP leaks/races
    try { this.cookie.remove('expires'); } catch {}
    this.storage.removeAuthProvider();
    this.storage.clear(isClearStorage, isPortal);
    this.storage.clearCopyTokens?.();
    this.access_token = null;
    this.refresh_token = null;
    this.username = null;
    this.idle.stop();
    this.modal.dismissAll();
    this.isRedirectPerformed = false;
    this.isProfileDataFetched = false;
    if (this.getRefreshToken() && !this.storage.getIsExternalLogin()) {
      this.subsink.add(
      this.revokeToken()
        .pipe(
          finalize(() =>
            this.resettingTheAuthDataAndNavigateToLoginScreen(
              setRedirectTo,
              isClearStorage,
              isPortal,
              forceDuringCallback
            )
          )
        )
        .subscribe(
          (resp) => {

          },
          (err) => {
            console.error('Error in Logout API!!');
          }
        ));
    } else {
      this.resettingTheAuthDataAndNavigateToLoginScreen(
        setRedirectTo,
        isClearStorage,
        isPortal,
        forceDuringCallback
      );
    }
    // **TODO: call logout api with refreshtoken here
  };

  resettingTheAuthDataAndNavigateToLoginScreen = (
    setRedirectTo: boolean = true,
    isClearStorage: boolean = false,
    isPortal: string | null = null,
    forceNavigateOnCallback: boolean = false
  ) => {
    let portalId = this.resolvePortalId(isPortal);
    const redirectTo = this.deriveRedirectTarget(setRedirectTo);
    this.handleLogoutPath(setRedirectTo, redirectTo, forceNavigateOnCallback);
    this.storage.clearBootstrapToken();
    this.storage.clear(isClearStorage, isPortal);
    this.storage.clearCopyTokens?.();
    this.access_token = null;
    this.username = null;
    this.refresh_token = null;
    this.tokenApiData = null;
    this.isLoggedIn = false;
    this.isProfileDataFetched = false;
    this.isRedirectPerformed = false;
    this.applicationService.clearSingleAppUserState();
    this.resetStore();
    this.clearTimeouts();
    this.route.getApplicationName();

    // Set flag AFTER storage.clear() to ensure it persists - this prevents silent login
    // Set in both sessionStorage and localStorage to ensure it persists
    try {
      sessionStorage.setItem('fromLogoutRoute', '1');
      localStorage.setItem('fromLogoutRoute', '1');
    } catch {}

    if (this.isOnStandaloneAuthScreen()) {
      return;
    }

    const target = this.resolveFinalRedirectTarget(redirectTo);
    this.navigateToLogin(portalId, setRedirectTo, target, forceNavigateOnCallback);
  };

  private readonly resolvePortalId = (isPortal: string | null): string | null => {
    let portalId = isPortal || this.storage.getPortalId();
    const currentAppKey = this.route.getApplicationKey();
    if (portalId && currentAppKey && currentAppKey !== portalId) {
      portalId = currentAppKey;
      this.storage.savePortalId(portalId);
    }
    return portalId as any;
  };

  private readonly sanitizeRedirectTarget = (target: string): string => {
    if (!target) return '';
    if (target.includes('/callback-handler')) return '';
    if (/^\/?auth\//.test(target.replace(/^\//, ''))) return '';
    return target;
  };

  private readonly deriveRedirectTarget = (setRedirectTo: boolean): string => {
    if (!setRedirectTo) return '';
    const qp = location.href.split('?')[1] ?? '';
    const parsed = decodeURIComponent(qp.replace('redirectTo=', ''));
    const raw = parsed || (this.route.currentRoute() !== '/' ? this.route.currentRoute() : window.location.pathname);
    return this.sanitizeRedirectTarget(raw);
  };

  private readonly persistRedirectTarget = (redirectTo: string) => {
    const sanitized = this.sanitizeRedirectTarget(redirectTo);
    if (sanitized) {
      this.storage.saveRedirectURL(sanitized);
      return;
    }
    this.storage.clearRedirectURL();
  };

  private readonly clearRedirectTarget = () => {
    this.storage.clearRedirectURL();
  };

  private readonly handleLogoutPath = (setRedirectTo: boolean, redirectTo: string, forceNavigateOnCallback: boolean) => {
    const logoutPath = '/auth/logout';
    const loginPath = '/auth/login';
    if (this.storage.getIsExternalLogin()) {
      this.storage.removeIsExternalLogin();
      if (!forceNavigateOnCallback) {
        // Auth0 logout should return to /auth/logout, which will then redirect to /auth/login
        this.auth0Service.logout({ logoutParams: { returnTo: `${window.location.origin}${logoutPath}` } });
      }
      if (setRedirectTo && redirectTo) this.persistRedirectTarget(redirectTo); else this.clearRedirectTarget();
    } else {
      if (setRedirectTo && redirectTo) this.persistRedirectTarget(redirectTo); else this.clearRedirectTarget();
      try {
        this.router.navigateByUrl(loginPath, { replaceUrl: true });
      } catch (err) {
        console.warn('Non-Auth0 logout: navigation to /auth/login failed', err);
      }
    }
  };

  private readonly isOnStandaloneAuthScreen = (): boolean => {
    const href = window.location.href;
    return href.includes('/auth/hidden-5b67012b-4bc3-43ca-accd-89e563b5c6f7-login') ||
      href.includes('/auth/register') ||
      href.includes('/auth/terms') ||
      href.includes('/auth/privacy') ||
      href.includes('/auth/contactBillingAdmin') ||
      href.includes('/auth/resetpassword') ||
      href.includes('/auth/resendmfacode') ||
      href.includes('/verification') ||
      href.includes('/auth/passwordrecovery');
  };

  private readonly resolveFinalRedirectTarget = (redirectTo: string): string => {
    if (!redirectTo || redirectTo === '/') {
      return this.storage.getRedirectURL() ?? '';
    }
    return redirectTo;
  };

  private readonly navigateToLogin = (
    portalId: string | null,
    setRedirectTo: boolean,
    redirectTo: string,
    forceNavigateOnCallback: boolean
  ) => {
    if (window.location.href.includes('/auth/login') && !forceNavigateOnCallback) return;
    if (!window.location.href.includes('/callback-handler') || forceNavigateOnCallback) {
      const queryParams = setRedirectTo && redirectTo ? { redirectTo } : {} as any;
      const go = (commands: any[]) => this.route.navigate(commands, { replaceUrl: true, queryParams });
      const extractAppFromRedirect = () => /apps\/([^/]+)\//.exec(redirectTo)?.[1] ?? null;
      if (portalId != null) {
        this.router.navigateByUrl('/', { skipLocationChange: true}).then(() => go(['auth', 'login', portalId])).catch(() => {});
        return;
      }
      if (redirectTo) {
        const appFromUrl = extractAppFromRedirect();
        this.router.navigateByUrl('/', { skipLocationChange: true}).then(() => go(appFromUrl ? ['auth', 'login', appFromUrl] : ['auth', 'login'])).catch(() => {});
        return;
      }
      this.router.navigateByUrl('/', { skipLocationChange: true}).then(() => go(['auth', 'login'])).catch(() => {});
    }
  };

  /**
   * Impersonate a user
   */
  impersonateUser = () => {
    let redirectTo: string | null = null;
    if(this.isImpersonateUserIssueFixEnabled) {
      // Capture the current app key so the impersonated session opens the same app's home page
      const appKey = this.route.getApplicationKey();
      redirectTo = appKey ? encodeURIComponent(`/apps/${appKey}`) : null;
  
      // Save the super admin's current app root so backToSuperAdmin can return there
      const returnAppKey = this.route.getApplicationKey();
      const returnUrl = returnAppKey ? `/apps/${returnAppKey}` : '/apps';
      this.storage.saveSuperAdminReturnUrl(returnUrl);
    }

    this.storage.copyTokens();
    this.isImpersonated = true;
    // Reset redirect gating and stale redirect target
    this.isRedirectPerformed = false;
    this.storage.clearRedirectURL();
    this.username = null;
    this.access_token = null;
    this.refresh_token = null;
    this.tokenApiData = null;
    this.isLoggedIn = false;
    this.isProfileDataFetched = false;
    this.applicationService.clearSingleAppUserState();
    this.loginLogoutSubject.next(true);
    this.resetStore();
    this.clearTimeouts();
    this.route.getApplicationName();
    this.navigationService.showGlobalNav.emit('hide');
    if (!this.route.currentRoute().includes('/auth/login') && !this.route.currentRoute().includes('/callback-handler'))
      this.router.navigateByUrl('/', { skipLocationChange: true}).then(() => {
        this.route.navigate(['auth', 'login'], {
          queryParams: redirectTo ? { redirectTo } : {},
          replaceUrl: true,
        });
      }).catch(() => {});
  };

  /**
   * Back to Super Admin
   */
  backToSuperAdmin = async () => {
    this.isImpersonated = false;
    await this.storage.restoreTokens();
    await this.resetStore();
    // Ensure next login redirect logic is not blocked and no stale redirect remains
    this.isRedirectPerformed = false;
    this.storage.clearRedirectURL();
    await this.cacheAuthData();
    this.tokenApiData = null;
    if(this.isImpersonateUserIssueFixEnabled) {
      this.storage.clearImpersonateId();
      const returnUrl = this.storage.getSuperAdminReturnUrl() ?? '/';
      this.storage.clearSuperAdminReturnUrl();
      this.windowRef.navigateTo(returnUrl);
    } else {
      this.windowRef.reload();
    }
  };

  /**
   * Reset all store states
   */
  resetStore = () => {
    this.store.dispatch(new ResetApplication());
    this.store.dispatch(new ResetAuth());
    this.store.dispatch(new ResetCompany());
    this.store.dispatch(new ResetField());
    this.store.dispatch(new ResetForms());
    this.store.dispatch(new ResetIgnatius());
    this.store.dispatch(new ResetImports());
    this.store.dispatch(new ResetPurges());
    this.store.dispatch(new ResetRelations());
    this.store.dispatch(new ResetReports());
    this.store.dispatch(new ResetNavigation());
    this.store.dispatch(new ResetUser());
    this.store.dispatch(new ResetApplicationUsers());
    this.store.dispatch(new ResetCopyApps());
    this.store.dispatch(new ResetDashboards());
    this.store.dispatch(new ResetPages());
    this.store.dispatch(new ResetRoles());
    this.store.dispatch(new ResetClient());
    this.store.dispatch(new ResetNotification());
  };

  /**
   * Sets login state according to fetched data
   * @returns true if both token and login api data is fetched
   */
  setLoginState = async () => {
    try {
      if (this.runtimeConfig.isFeatureEnabled()) {
        // Save bootstrap token immediately (before config fetch) so it's available for APP_INITIALIZER on refresh
        if (this.tokenApiData?.access_token) {
          this.storage.saveBootstrapToken(this.tokenApiData.access_token);
        }

        this.runtimeConfig.loadFromLoginResponse(this.tokenApiData);
        // Fallback: if config not in token response, fetch from config API
        if (!this.runtimeConfig.isConfigLoaded() && this.tokenApiData?.access_token) {
          try {
            await Promise.race([
              this.runtimeConfig.loadFromApiWithToken(this.tokenApiData.access_token),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Config request timed out')), 10000)
              ),
            ]);
          } catch (configErr: unknown) {
            console.error('RuntimeConfigService: failed to load config after login', configErr);
            this.alert.toast(
              ToastTypes.ERROR,
              'Configuration Error',
              'Unable to load application configuration. Please try again or contact support.'
            );
            this.loadingService.loadingState = false;
            this.store.dispatch(new ResetAuth());
            this.store.dispatch(new LoginUserFailed({
              status: 0,
              statusText: 'Configuration Error',
              error: {
                error: 'config_load_failed',
                error_description: 'Unable to load application configuration. Please try again or contact support.',
                Message: 'Configuration load failed'
              }
            } as any));
            return;
          }
        }
        if (!this.runtimeConfig.isConfigLoaded()) {
          this.alert.toast(
            ToastTypes.ERROR,
            'Configuration Error',
            'Application configuration is not available. Please ensure the backend returns SecretKey and FlexKey in the login response or config API.'
          );
          this.loadingService.loadingState = false;
          this.store.dispatch(new ResetAuth());
          this.store.dispatch(new LoginUserFailed({
            status: 0,
            statusText: 'Configuration Error',
            error: {
              error: 'config_not_available',
              error_description: 'Application configuration is not available. Please ensure the backend returns SecretKey and FlexKey in the login response or config API.',
              Message: 'Configuration not available'
            }
          } as any));
          return;
        }
      }
      this.username = this.tokenApiData!.userName;
      this.access_token = this.tokenApiData!.access_token;
      this.refresh_token = this.tokenApiData!.refresh_token;
      this.storage.saveToken(
        this.tokenApiData!.access_token!,
        this.tokenApiData!.refresh_token!
      );
      this.storage.saveUsername(this.tokenApiData!.userName!);
      this.isLoggedIn = true;

      // SecretKey is now guaranteed to be available (either from runtime config
      // or hardcoded environment). Process any buffered login-response data that
      // requires encrypted storage (workflow token + subscription flags).
      if (this.pendingLoginResponse) {
        const pending = this.pendingLoginResponse;
        this.pendingLoginResponse = null;
        this.storage.saveSubscriptionFlags(
          pending.response.IsBillingAdmin.toString(),
          pending.response.IsPendingPayment.toString(),
          pending.response.IsSubscriptionExpired.toString(),
          pending.response.IsSubscriptionFreeTrial.toString(),
          pending.response.Roles.Id
        );
        if (this.storage.getIsSubscriptionExpired() == 'true') {
          if (this.storage.getUserRole() == 'admin') {
            this.route.redirect('/user/billing');
          } else {
            this.route.redirect('/auth/contactBillingAdmin');
            this.isLoggedIn = false;
            this.isProfileDataFetched = false;
            return;
          }
        }
      }

      if(!this.isProfileDataFetched){
        this.store.dispatch(new Profile());
        this.isProfileDataFetched = true;
      }
      this.idle.watch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed. Please try again.';
      this.alert.toast(ToastTypes.ERROR, 'Login Error', msg);
      this.loadingService.loadingState = false;
      this.store.dispatch(new ResetAuth());
      this.store.dispatch(new LoginUserFailed({ error: { Message: msg } } as any));
    }
  };

  setMLState = () => {
    this.ml_access_token = this.mlTokenApiData!.access_token;
    this.ml_refresh_token = this.mlTokenApiData!.refresh_token;
    this.storage.saveMLToken(
      this.mlTokenApiData!.access_token!,
      this.mlTokenApiData!.refresh_token!
    );
  };

  setTokenData = (data: any) => {
    this.tokenData = data;
  };

  /**
   * Subscriptions for auth related apis
   */
  subscriptions = () => {
    this.subscribeTokenSubscription();
    // token and login API
    this.subsink.add(
      combineLatest([this.store.select(getLoginResponse)])
        .pipe(filter((authState) => !!authState[0]))
        .subscribe((response: any) => {
          if (
            response != null &&
            !!response[0].error &&
            response[0].status != undefined &&
            (response[0].status == 400 || response[0].status == 401) &&
            response[0].error != "Passcode failed."
          ) {
            this.alert.toast(ToastTypes.ERROR, '', response[0].error.Message);
          }
          if (
            response != null &&
            response[0].Success != undefined &&
            response[0].Success &&
            response[0].status != 400
          ) {

            if(!this.tokenSubscription.length){
              this.subscribeTokenSubscription();
            }

            if(this.tokenData && Object.keys(this.tokenData).length){
              this.store.dispatch(new Token(this.tokenData, environment.apiUrl));
              // Buffer credentials and login response so that workflow token fetch
              // and subscription flags are saved inside setLoginState() AFTER the
              // runtime config (SecretKey) is loaded — avoids the race where
              // encrypted storage silently drops values when the key isn't ready yet.
              this.pendingLoginResponse = {
                tokenData: { ...this.tokenData },
                response: response[0]
              };
              this.tokenData = null;
            }
            this.storage.clearRedirectURL();
          }
        })
    );

    // error
    this.subsink.add(
      this.store
        .pipe(
          select(getAuthError),
          filter((data) => !!data && !!data.error.Message)
        )
        .subscribe((data: any) => {
          this.alert.toast(ToastTypes.ERROR, '', data.error.Message);
          this.logout(true, false, this.storage.getPortalId());
        })
    );
    // user profile
    this.subsink.add(
      this.store
        .pipe(
          select(getUserProfile),
          filter((data) => !!data)
        )
        .subscribe((data) => {
          this.userProfile = data;
          this.isImpersonated = false;
          if(this.storage.getIsExternalLogin() && data && Object.keys(data).length){
            this.storage.saveUserRole(data?.Role || '');
          }
          this.userDataSubject.next(0)
          // this.showExpiryTimeInTitle();
          // this.logoutOnSessionExpire();
          const onAuthOrCallback = window.location.href.includes('/auth') || window.location.pathname === '/callback-handler';
          if (onAuthOrCallback && !this.isRedirectPerformed){
            this.isRedirectPerformed = true;
            // Prefer explicit redirectTo when present (including impersonation)
            const redirectToParam = this.route.getParam('redirectTo');
            if (redirectToParam != null && redirectToParam || this.storage.getImpersonateId()) {
              this.route.navigate(this.route.redirectTo(), { replaceUrl: true });
              return;
            }
            // Otherwise, direct to the user's app if known
            let portalId = this.storage.getPortalId();
            if (!portalId && this.route.paramMap && this.route.paramMap.appId) {
              portalId = this.route.paramMap.appId;
              this.storage.savePortalId(portalId);
            }
            const safePortalId = sanitizeAppPathSegmentForRedirect(portalId);
            if (safePortalId) {
              // Probe single-app status in the background so the sidebar hides App Library
              // for single-app users even when redirected via a portalId in the login URL.
              this.applicationService.resolveSingleAppPostLogin$().pipe(take(1)).subscribe();
              this.route.redirect(`/apps/${safePortalId}`);
            } else {
              this.applicationService
                .resolveSingleAppPostLogin$()
                .pipe(take(1))
                .subscribe((resolution) => {
                  if (
                    resolution &&
                    typeof resolution === 'object' &&
                    'appKey' in resolution
                  ) {
                    const safeAppKey = sanitizeAppPathSegmentForRedirect(
                      (resolution as { appKey: unknown }).appKey
                    );
                    if (safeAppKey) {
                      this.route.redirect(`/apps/${safeAppKey}`);
                    } else {
                      this.route.redirect('/apps');
                    }
                  } else {
                    this.route.redirect('/apps');
                  }
                });
            }
          }
        })
    );
  };

  subscribeTokenSubscription() {
    this.tokenSubscription.push(
      combineLatest([this.store.select(getAuth)])
        .pipe(filter((authState) => !!authState[0]))
        .subscribe((authState) => {
          this.saveExpiryToCookie();
          if(authState[0]!['as:client_id'] === 'IAFConsulting'){
            this.tokenApiData = authState[0]!;
            this.setLoginState().catch(() => {
              // Error already handled in setLoginState (toast + LoginUserFailed)
            });
          }
        })
    );
  }

  /**
   * Headers
   * @param contentType type of content in req body
   * @returns headers
   */
  getHeaders = (
    contentType: string = CONTENT_TYPES.APPLICATION_JSON,
    addToken: boolean = true
  ) => {
    let headers = new HttpHeaders();
    headers = headers.append('content-type', contentType);
    if (addToken)
      headers = headers.append(
        'Authorization',
        `Bearer ${this.getAccessToken()}`
      );
    return headers;
  };

  /**
   * Save expiry unix timestamp to cookie
   */
  saveExpiryToCookie = () => {
    this.cookie.put(
      'expires',
      moment(this.tokenApiData?.['.expires']!).format('x')
    );
  };

  /**
   * Set timeout function to update title with time left for session
   */
  showExpiryTimeInTitle = () => {
    let state = 0;
    this.titleInterval = setInterval(() => {
      if (state === 0) {
        state = 1;
        this.route.getApplicationName();
      } else {
        state = 0;
        const text = moment(this.expires).fromNow();
        this.route.changeTitle(
          `Session expire${text.includes('ago') ? 'd' : 's'} ${text}`
        );
      }
    }, 10000);
  };


  /**
   * Clear timeouts
   */
  clearTimeouts = () => {
    if (this.titleInterval) clearInterval(this.titleInterval);
    if (this.logoutTimeout) clearTimeout(this.logoutTimeout);
  };

  /**
   * Checks if token is expired
   * @returns true if token is expired
   */
  get isTokenExpired() {
    return moment(this.expires).diff(moment()) < 0;
  }

  /**
   * Refresh token
   * @returns promise
   */
  refreshToken = async (): Promise<string> => {
    return Promise.resolve(this.getAccessToken() as string);
  };

  getRefreshTokenByApi = async (): Promise<string> => {
    const payload: any = {
      grant_type: 'refresh_token',
      refresh_token: this.getRefreshToken()!,
      client_id: environment.clientId,
      scope: 'email profile roles offline_access',
    };
    let params = new HttpParams();
    for (const creds in payload) params = params.set(creds, payload[creds]);
    // if (!this.refreshTokenPromise)
    this.refreshTokenPromise = await this.http
      .post<TokenAPI>(`${environment.apiUrl}${this.TOKEN_URL}`, params, {
        headers: this.loginService.makeHeaders(CONTENT_TYPES.URLENCODED),
      })
      .pipe(
        tap((tokenAPI: TokenAPI) => {
          this.tokenApiData = tokenAPI;
          this.isProfileDataFetched = false;
          this.saveExpiryToCookie();
          this.setLoginState();
        }),
        map((tokenAPI: TokenAPI) => tokenAPI.access_token)
      )
      .toPromise();
    return this.refreshTokenPromise;
  };

  private get expires() {
    return +this.cookie.get('expires') || 0;
  }

  idleSubscribe = () => {
    this.idle.idle = this.IDLE_TIME;
    this.idle.idleTimeout = this.IDLE_TIMEOUT;
    this.subsink.add(
    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.title.setTitle(
        `${new Date(countdown * 1000)
          .toISOString()
          .substr(14, 5)} minutes idle time remaining before being logged out`
      );
    }),
    this.idle.onIdleEnd.subscribe(() => {
      this.route.getApplicationName();
    }),
    this.idle.onTimeout.subscribe(() => {
      // if (localStorage.getItem('token'))
      const modal = this.modal.open(RefreshTokenModalComponent, {
        windowClass: 'modal-custom',
        centered: true,
        backdrop: 'static',
      });
      this.subsink.add(
      modal.componentInstance.refreshToken.subscribe((res: any) => {
        if (res === 'true') {
          this.refreshToken();
        }
      }),
      modal.componentInstance.logout.subscribe((res: any) => {
        if (res === 'true') {
          this.logout(true, false, this.storage.getPortalId());
        }
      }));
      // this.logout();
    }));
  };
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  /**
   * Fetch config from API (using token) then set tokens. Use for Auth0/external login flow.
   * If config API fails but config was already loaded (e.g. from APP_INITIALIZER with cookies), proceed.
   */
  async fetchConfigAndSetTokens(tokenData: any): Promise<void> {
    if (this.runtimeConfig.isFeatureEnabled()) {
      const token = tokenData?.access_token ?? tokenData?.id_token;
      if (token) {
        this.storage.saveBootstrapToken(token);
      }
      if (token) {
        try {
          await this.runtimeConfig.loadFromApiWithToken(token);
        } catch {
          // Config API may fail; continue if config was already loaded (e.g. from APP_INITIALIZER)
        }
      }
      if (!this.runtimeConfig.isConfigLoaded()) {
        this.alert.toast(
          ToastTypes.ERROR,
          'Configuration Error',
          'Unable to load application configuration. Please try again or contact support.'
        );
        this.loadingService.loadingState = false;
        throw new Error('Config not loaded');
      }
    }
    this.setAllTokens(tokenData);
  }

  setAllTokens(tokenData:any){
    if (tokenData.access_token && tokenData.id_token) {
      this.access_token = tokenData.access_token;
      this.refresh_token = tokenData.access_token;
      this.storage.saveToken(tokenData.access_token, tokenData.access_token);
      this.isLoggedIn = true;
    } else {
      this.access_token = tokenData.access_token;
      this.refresh_token = tokenData.refresh_token || tokenData.access_token;
      this.storage.saveToken(tokenData.access_token, tokenData.refresh_token || tokenData.access_token);
      this.isLoggedIn = true;
    }
  }

  checkIfUserExist(payload:any){
    return this.authAPISerive.checkUserExist(payload).toPromise();
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
    this.tokenSubscription.forEach((s) => {
      s.unsubscribe();
    });
    this.tokenSubscription = [];
  }
}
